import type { Notebook, Tag as TagRow } from "@engram/core";
import {
  Book, BookCopy, BookPlus, BookUser, CirclePlus, Download, ChevronDown, ChevronRight, ChevronsLeft, Ellipsis, FileImage, FilePlus2, FileText, Monitor,
  MessageSquare, Moon, MoreHorizontal, Paperclip, Plus, RefreshCw, Search, LogOut, Settings, SlidersHorizontal, Star, Sun, Tag, TagIcon, Trash2, X,
} from "lucide-react";
import { Fragment, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { collator } from "./format";
import { logout } from "../auth";
import { createNote, createNotebook, createTag, notebookNameError, removeShortcut, reorderShortcuts, setSetting, tagNameError } from "../db/actions";
import { db, useQuery, useSyncState } from "../db/client";
import { Icon } from "./Logo";
import { DevicesDialog } from "./Devices";
import { FeedbackDialog } from "./Feedback";
import { ExportDialog } from "./ExportLazy";
import { queueFilesForNote } from "./editor/Editor";
import { Menu, Prompt, pointAnchor, useAnchor, type Anchor } from "./Menu";
import { recentNoteIds } from "./opens";
import { ShortcutIcon, ShortcutsDialog, useShortcuts, type ShortcutRow } from "./Shortcuts";
import { usePrefs } from "./prefs";
import { openTaskDialog } from "./TaskDialog";
import { shortcutHref } from "./Shell";
import { newNoteHere } from "./newNote";
import { CustomizeNav, navIcon, openTodayNote, useNav, type NavId } from "./navConfig";
import { buildTree, type Tree } from "./notebookTree";
import { Avatar, setDisplayName, useProfile } from "./profile";

export function Sidebar({ tagsOpen, onToggleTags, onSignedOut }: { tagsOpen: boolean; onToggleTags: () => void; onSignedOut: () => void }) {
  const [path] = useLocation();
  const prefs = usePrefs();
  const [dialog, setDialog] = useState<null | "notebook" | "tag">(null);

  const counts = useQuery<{ all: number; trash: number }>(
    `SELECT SUM(trashed_at IS NULL) AS "all", SUM(trashed_at IS NOT NULL) AS trash FROM notes WHERE deleted = 0`,
    [],
    ["notes"],
  )?.[0];

  const isActive = (p: string) => path === p || path.startsWith(p + "/");
  const [, navigate] = useLocation();
  const nav = useNav("sidebar");
  const group = (id: NavId) => (id === "notebooks" || id === "tags" ? 1 : 0);
  const item = (id: NavId): ReactNode => {
    switch (id) {
      case "home": return <NavItem href="/home" icon={navIcon(id, 16)} label="Home" active={isActive("/home")} />;
      case "shortcuts": return <Shortcuts />;
      case "notes": return <NavItem href="/notes" icon={navIcon(id, 16)} label="Notes" active={isActive("/notes")} count={counts?.all} />;
      case "tasks": return <NavItem href="/tasks" icon={navIcon(id, 16)} label="Tasks" active={isActive("/tasks")} />;
      case "files": return <NavItem href="/files" icon={navIcon(id, 16)} label="Files" active={isActive("/files")} />;
      case "today":
        return (
          <button className="nav-item" title="Today's note (Ctrl+Alt+D)" onClick={() => void openTodayNote(navigate)}>
            <span className="nav-icon">{navIcon(id, 16)}</span>
            <span className="nav-label">Today</span>
          </button>
        );
      case "notebooks": return <NotebooksTree path={path} onNew={() => setDialog("notebook")} />;
      case "tags": return <TagsTree open={tagsOpen} onToggle={onToggleTags} onNew={() => setDialog("tag")} />;
      default: return null;
    }
  };

  return (
    <nav className="sidebar">
      <div className="sidebar-top">
        <Link href="/notes" className="brand">
          <Icon size={22} />
          <span className="wordmark">engram</span>
        </Link>
        <button className="icon-btn subtle" title="Collapse sidebar" onClick={() => prefs.set({ sidebarCollapsed: !prefs.sidebarCollapsed })}>
          <ChevronsLeft size={16} />
        </button>
      </div>

      <SearchBox />
      <CreateRow onNew={setDialog} />

      <div className="nav-scroll">
        {/* Customizable (More > Customize sidebar); a gap between the pages and the notebooks/tags trees. */}
        {nav.map((id, i) => (
          <Fragment key={id}>
            {i > 0 && group(id) !== group(nav[i - 1]!) && <div className="nav-gap" />}
            {item(id)}
          </Fragment>
        ))}
        <div className="nav-gap" />
        <More trash={counts?.trash ?? 0} active={isActive("/trash")} />
      </div>

      <Account onSignedOut={onSignedOut} />
      <NewThing kind={dialog} onClose={() => setDialog(null)} />
    </nav>
  );
}

/** New notebook / new tag prompts (sidebar ⊕ buttons, the create menu). */
function NewThing({ kind, onClose }: { kind: null | "notebook" | "tag"; onClose: () => void }) {
  const [, navigate] = useLocation();
  if (kind === "notebook")
    return <Prompt title="Create new notebook" label="Name" confirm="Create" validate={notebookNameError("notebook")} onSubmit={async (v) => navigate(`/notebook/${await createNotebook(v)}`)} onClose={onClose} />;
  if (kind === "tag") return <Prompt title="Create new tag" label="Name" confirm="Create" validate={tagNameError()} onSubmit={async (v) => navigate(`/tag/${await createTag(v)}`)} onClose={onClose} />;
  return null;
}

/** Evernote's sidebar Search opens the search window (recent and saved searches, best matches as you type). */
function SearchBox() {
  return (
    <button className="search" onClick={() => window.dispatchEvent(new Event("engram:search"))} aria-label="Search notes">
      <Search size={14} />
      <span>Search</span>
    </button>
  );
}

/** "+ Note", New task, and Evernote's "Create new" menu (a note from an image or file, a notebook, a tag). */
function CreateRow({ onNew }: { onNew: (k: "notebook" | "tag") => void }) {
  const [path, navigate] = useLocation();
  const more = useAnchor();
  const files = useRef<HTMLInputElement>(null);
  const [accept, setAccept] = useState("");
  const pick = (kind: "image" | "file") => {
    setAccept(kind === "image" ? "image/*" : "");
    setTimeout(() => files.current?.click(), 0);
  };
  return (
    <div className="create-row">
      <button
        className="btn primary pill"
        onClick={() => void newNoteHere(path, navigate)}
      >
        <FilePlus2 size={17} strokeWidth={2.2} /> Note
      </button>
      <button className="round-btn" title="New task" aria-label="New task" onClick={() => openTaskDialog()}>
        <CirclePlus size={16} className="ic-task" />
      </button>
      <button className="round-btn" title="Create new" aria-label="Create new" onClick={more.open}>
        <Ellipsis size={16} />
      </button>
      {more.anchor && (
        <Menu
          anchor={more.anchor}
          onClose={more.close}
          entries={[
            { heading: "Create new" },
            { label: "Image", icon: <FileImage size={14} />, onSelect: () => pick("image") },
            { label: "File", icon: <Paperclip size={14} />, onSelect: () => pick("file") },
            { separator: true },
            { label: "Notebook", icon: <BookPlus size={14} />, onSelect: () => onNew("notebook") },
            { label: "Tag", icon: <TagIcon size={14} />, onSelect: () => onNew("tag") },
          ]}
        />
      )}
      <input
        ref={files}
        type="file"
        multiple
        hidden
        accept={accept}
        onChange={async (e) => {
          const list = [...(e.target.files ?? [])];
          e.target.value = "";
          if (!list.length) return;
          const id = await createNote({ title: list.length === 1 ? list[0]!.name.replace(/\.[^.]+$/, "") : "" });
          queueFilesForNote(id, list);
          navigate(`/notes?n=${id}`);
        }}
      />
    </div>
  );
}

function NavItem({ href, icon, label, active, count, indent = 0, after }: { href: string; icon: ReactNode; label: string; active?: boolean; count?: number; indent?: number; after?: ReactNode }) {
  return (
    <Link href={href} className={`nav-item${active ? " active" : ""}`} style={indent ? { paddingLeft: 12 + indent * 16 } : undefined}>
      <span className="nav-icon">{icon}</span>
      <span className="nav-label">{label}</span>
      {count !== undefined && <span className="nav-count">{count}</span>}
      {after}
    </Link>
  );
}

/**
 * A sidebar section like Evernote's Shortcuts / Notebooks / Tags rows: on hover the icon turns into the
 * expand chevron, and sections that can create something show ⊕ on the right.
 */
function SectionRow({ icon, label, open, onToggle, onClick, active, onAdd, addLabel, className = "" }: { icon: ReactNode; label: string; open: boolean; onToggle: () => void; onClick: () => void; active?: boolean; onAdd?: () => void; addLabel?: string; className?: string }) {
  return (
    <div className={`nav-item section-row${active ? " active" : ""}${open ? " open" : ""}${className ? ` ${className}` : ""}`}>
      <button
        className="section-toggle"
        aria-label={`${open ? "Collapse" : "Expand"} ${label}`}
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
      >
        <span className="nav-icon section-icon">{icon}</span>
        <span className="section-chev">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
      </button>
      <button className="nav-link section-label" onClick={onClick}>
        <span className="nav-label">{label}</span>
      </button>
      {onAdd && (
        <button className="section-add" title={addLabel} aria-label={addLabel} onClick={onAdd}>
          <CirclePlus size={15} className="ic-create" />
        </button>
      )}
    </div>
  );
}

function Shortcuts() {
  const { expanded, toggle } = usePrefs();
  const open = !!expanded.shortcuts;
  const rows = useShortcuts();
  const [path] = useLocation();
  const [dragId, setDragId] = useState<string | null>(null);
  const [over, setOver] = useState<{ id: string; after: boolean } | null>(null);
  const [menu, setMenu] = useState<{ row: ShortcutRow; anchor: Anchor } | null>(null);
  const [managing, setManaging] = useState(false);
  const recentIds = open ? recentNoteIds(5) : [];
  const recent = useQuery<{ id: string; title: string }>(
    open && recentIds.length ? `SELECT id, title FROM notes WHERE deleted = 0 AND trashed_at IS NULL AND id IN (${recentIds.map(() => "?").join(",")})` : null,
    recentIds,
    ["notes"],
  );

  // Drag shortcuts in the sidebar to reorder them.
  const drop = () => {
    if (!rows || !dragId || !over || dragId === over.id) return;
    const ids = rows.map((r) => r.id).filter((id) => id !== dragId);
    const at = ids.indexOf(over.id) + (over.after ? 1 : 0);
    ids.splice(at, 0, dragId);
    void reorderShortcuts(ids);
  };

  return (
    <>
      {/* Evernote: clicking Shortcuts opens and closes the list. */}
      <SectionRow icon={<Star size={16} />} label="Shortcuts" open={open} onToggle={() => toggle("shortcuts")} onClick={() => toggle("shortcuts")} />
      {open &&
        rows?.map((s) => (
          <div
            key={s.id}
            draggable
            className={`drag-row${dragId === s.id ? " dragging" : ""}${over?.id === s.id ? (over.after ? " drop-after" : " drop-before") : ""}`}
            onDragStart={(e) => {
              setDragId(s.id);
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData("text/plain", s.label ?? "");
            }}
            onDragOver={(e) => {
              if (!dragId) return;
              e.preventDefault();
              const r = e.currentTarget.getBoundingClientRect();
              setOver({ id: s.id, after: e.clientY > r.top + r.height / 2 });
            }}
            onDrop={(e) => {
              e.preventDefault();
              drop();
            }}
            onDragEnd={() => {
              setDragId(null);
              setOver(null);
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              setMenu({ row: s, anchor: pointAnchor(e.clientX, e.clientY) });
            }}
          >
            <NavItem
              href={shortcutHref(s.kind, s.target_id)}
              indent={1}
              icon={<ShortcutIcon kind={s.kind} />}
              label={s.label || "Untitled"}
              active={s.kind === "note" ? path === "/shortcuts" && location.search.includes(s.target_id) : path === shortcutHref(s.kind, s.target_id)}
              after={
                <button
                  className="nav-remove"
                  title="Remove from Shortcuts"
                  aria-label="Remove from Shortcuts"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void removeShortcut(s.kind, s.target_id);
                  }}
                >
                  <X size={13} />
                </button>
              }
            />
          </div>
        ))}
      {open && rows?.length === 0 && <div className="nav-empty">Add shortcuts from a note's ··· menu</div>}
      {open && recent && recent.length > 0 && (
        <>
          <div className="nav-heading">Recent notes</div>
          {recentIds
            .map((id) => recent.find((r) => r.id === id))
            .filter((r): r is { id: string; title: string } => !!r)
            .map((r) => (
              <NavItem key={r.id} href={`/note/${r.id}`} indent={1} icon={<FileText size={14} />} label={r.title || "Untitled"} />
            ))}
        </>
      )}
      {menu && (
        <Menu
          anchor={menu.anchor}
          onClose={() => setMenu(null)}
          entries={[
            { label: "Remove from Shortcuts", icon: <X size={14} />, onSelect: () => void removeShortcut(menu.row.kind, menu.row.target_id) },
            { label: "Reorder shortcuts…", onSelect: () => setManaging(true) },
          ]}
        />
      )}
      {managing && <ShortcutsDialog onClose={() => setManaging(false)} />}
    </>
  );
}

function NotebooksTree({ path, onNew }: { path: string; onNew: () => void }) {
  const { expanded, toggle } = usePrefs();
  const [, navigate] = useLocation();
  const open = !!expanded.notebooks;
  const rows = useQuery<Notebook>(open ? "SELECT * FROM notebooks WHERE deleted = 0" : null, [], ["notebooks"]);
  const def = useQuery<{ value: string }>(open ? "SELECT value FROM settings WHERE id = 'defaultNotebook' AND deleted = 0" : null, [], ["settings"])?.[0];
  const defaultId = def ? (JSON.parse(def.value) as string) : null;
  const tree = buildTree(rows ?? []);
  const bookItem = (nb: Notebook, indent: number) => (
    <NavItem
      key={nb.id}
      href={`/notebook/${nb.id}`}
      indent={indent}
      icon={nb.id === defaultId ? <BookUser size={14} aria-label="Default notebook" /> : <Book size={14} />}
      label={nb.name}
      active={path === `/notebook/${nb.id}`}
    />
  );
  return (
    <>
      <SectionRow icon={<Book size={16} />} label="Notebooks" open={open} onToggle={() => toggle("notebooks")} onClick={() => navigate("/notebooks")} active={path === "/notebooks"} onAdd={onNew} addLabel="New notebook" />
      {open &&
        tree.top.map((r) => (r.kind === "stack" ? <StackItem key={r.id} stack={r} tree={tree} depth={1} path={path} item={bookItem} /> : bookItem(r, 1)))}
      {open && (
        <button className="nav-item nav-create" style={{ paddingLeft: 28 }} onClick={onNew}>
          <span className="nav-icon"><BookPlus size={14} /></span>
          <span className="nav-label">New Notebook</span>
        </button>
      )}
    </>
  );
}

function StackItem({ stack, tree, depth, path, item }: { stack: Notebook; tree: Tree<Notebook>; depth: number; path: string; item: (nb: Notebook, indent: number) => ReactNode }) {
  const { expanded, toggle } = usePrefs();
  const open = expanded[`stack:${stack.id}`] ?? true;
  const shift = (depth - 1) * 16; // stacks inside stacks (Customize sidebar > nesting)
  return (
    <>
      <div className={`nav-item${path === `/stack/${stack.id}` ? " active" : ""}`} style={{ paddingLeft: 12 + shift }}>
        <button
          className="expander"
          style={{ left: 10 + shift }}
          aria-label={open ? "Collapse" : "Expand"}
          onClick={(e) => {
            e.preventDefault();
            toggle(`stack:${stack.id}`);
          }}
        >
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <Link href={`/stack/${stack.id}`} className="nav-link" style={{ paddingLeft: 16 }}>
          <span className="nav-icon"><BookCopy size={14} /></span>
          <span className="nav-label">{stack.name}</span>
        </Link>
      </div>
      {open &&
        tree.children(stack.id).map((c) =>
          c.kind === "stack" ? <StackItem key={c.id} stack={c} tree={tree} depth={depth + 1} path={path} item={item} /> : item(c, depth + 1),
        )}
    </>
  );
}

/** Tags: the label opens Evernote's tags flyout; the chevron lists them here, ending with New Tag. */
function TagsTree({ open: flyoutOpen, onToggle, onNew }: { open: boolean; onToggle: () => void; onNew: () => void }) {
  const { expanded, toggle } = usePrefs();
  const [path] = useLocation();
  const open = !!expanded.tags;
  const rows = useQuery<TagRow>(open ? "SELECT * FROM tags WHERE deleted = 0" : null, [], ["tags"]);
  const all = (rows ?? []).sort((a, b) => collator.compare(a.name, b.name));
  const tree = (parent: string | null, depth: number): ReactNode[] =>
    all
      .filter((t) => (t.parent_id ?? null) === parent || (depth === 1 && parent === null && t.parent_id && !all.some((x) => x.id === t.parent_id)))
      .flatMap((t) => [
        <NavItem key={t.id} href={`/tag/${t.id}`} indent={depth} icon={<Tag size={14} />} label={t.name} active={path === `/tag/${t.id}`} />,
        ...tree(t.id, depth + 1),
      ]);
  return (
    <>
      {/* nav-tags: the flyout's outside-click check leaves this row to toggle it (TagsPanel.tsx). */}
      <SectionRow className="nav-tags" icon={<Tag size={16} />} label="Tags" open={open} onToggle={() => toggle("tags")} onClick={onToggle} active={flyoutOpen} onAdd={onNew} addLabel="New tag" />
      {open && tree(null, 1)}
      {open && (
        <button className="nav-item nav-create" style={{ paddingLeft: 28 }} onClick={onNew}>
          <span className="nav-icon"><Plus size={14} /></span>
          <span className="nav-label">New Tag</span>
        </button>
      )}
    </>
  );
}

/** Evernote keeps Trash under "More". */
function More({ trash, active }: { trash: number; active: boolean }) {
  const m = useAnchor();
  const [, navigate] = useLocation();
  const [customize, setCustomize] = useState(false);
  return (
    <>
      <button className={`nav-item${active ? " active" : ""}`} onClick={m.open}>
        <span className="nav-icon"><MoreHorizontal size={16} /></span>
        <span className="nav-label">More</span>
      </button>
      {m.anchor && (
        <Menu
          anchor={m.anchor}
          side="right"
          onClose={m.close}
          entries={[
            { label: "Trash", icon: <Trash2 size={14} />, hint: trash ? String(trash) : undefined, onSelect: () => navigate("/trash") },
            { separator: true },
            { label: "Customize sidebar…", icon: <SlidersHorizontal size={14} />, onSelect: () => setCustomize(true) },
            { label: "Settings", icon: <Settings size={14} />, onSelect: () => navigate("/settings") },
          ]}
        />
      )}
      {customize && <CustomizeNav kind="sidebar" onClose={() => setCustomize(false)} />}
    </>
  );
}

export function Account({ onSignedOut, compact }: { onSignedOut: () => void; compact?: boolean }) {
  const sync = useSyncState();
  const menu = useAnchor();
  const [, navigate] = useLocation();
  const prefs = usePrefs();
  const [naming, setNaming] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [devices, setDevices] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [feedback, setFeedback] = useState(false);
  const profile = useProfile();
  const name = profile.name;
  const status =
    sync.status === "syncing" ? "Syncing…" :
    sync.status === "offline" ? (sync.pending ? `Offline · ${sync.pending} to sync` : "Offline") :
    sync.status === "error" ? "Sync error" :
    sync.pending ? `${sync.pending} to sync` : "Synced";
  return (
    <div className={compact ? "account compact" : "account"}>
      <button className="account-btn" onClick={menu.open} title={compact ? `${name || "Engram"} · ${status}` : undefined}>
        <Avatar profile={profile} warn={sync.status === "offline" || sync.status === "error"} />
        {!compact && (
          <span className="account-text">
            <span className="account-name">{name || "Engram"}</span>
            <span className={`account-status${sync.status === "error" ? " bad" : ""}`} title={sync.error}>{status}</span>
          </span>
        )}
      </button>
      {menu.anchor && (
        <Menu
          anchor={menu.anchor}
          side={compact ? "right" : "top"}
          onClose={menu.close}
          entries={[
            // Who's signed in, like Evernote's account menu: photo, name, email (and the sync state on the rail).
            {
              node: (
                <div className="menu-account">
                  <Avatar profile={profile} size={36} />
                  <span className="menu-account-text">
                    <span className="menu-account-name">{name || "Engram"}</span>
                    {profile.email && <span className="menu-account-email">{profile.email}</span>}
                    {compact && <span className={`menu-account-email${sync.status === "error" ? " bad" : ""}`}>{status}</span>}
                  </span>
                </div>
              ),
            },
            { separator: true },
            { label: "Settings", icon: <Settings size={14} />, hint: "Ctrl+,", onSelect: () => navigate("/settings") },
            { label: "Sync now", icon: <RefreshCw size={14} />, onSelect: () => void db.sync() },
            { label: name ? "Change display name" : "Set display name", onSelect: () => setNaming(true) },
            { label: "Export all notes…", icon: <Download size={14} />, onSelect: () => setExporting(true) },
            { label: "Devices & tokens…", onSelect: () => setDevices(true) },
            { label: "Send feedback…", icon: <MessageSquare size={14} />, onSelect: () => setFeedback(true) },
            ...(compact ? [{ label: "Customize navigation…", icon: <SlidersHorizontal size={14} />, onSelect: () => setCustomize(true) }] : []),
            { separator: true },
            { heading: "Theme" },
            { label: "System", icon: <Monitor size={14} />, checked: prefs.theme === "system", onSelect: () => prefs.set({ theme: "system" }) },
            { label: "Light", icon: <Sun size={14} />, checked: prefs.theme === "light", onSelect: () => prefs.set({ theme: "light" }) },
            { label: "Dark", icon: <Moon size={14} />, checked: prefs.theme === "dark", onSelect: () => prefs.set({ theme: "dark" }) },
            { separator: true },
            {
              label: "Sign out",
              icon: <LogOut size={14} />,
              onSelect: async () => {
                // Signing out clears this device's copy, so unsynced edits would be lost: sync first, and ask if
                // anything is still waiting (offline, or the server refused it).
                const s = await db.sync();
                if (s.pending > 0 || s.status !== "idle") {
                  const what = s.pending ? `${s.pending} change${s.pending > 1 ? "s" : ""} on this device haven't synced` : "This device couldn't finish syncing";
                  if (!confirm(`${what}. Signing out deletes them. Sign out anyway?`)) return;
                }
                await logout();
                await db.reset();
                onSignedOut();
              },
            },
          ]}
        />
      )}
      {devices && <DevicesDialog onClose={() => setDevices(false)} />}
      {feedback && <FeedbackDialog onClose={() => setFeedback(false)} />}
      {customize && <CustomizeNav kind="rail" onClose={() => setCustomize(false)} />}
      {exporting && <ExportDialog scope={{ kind: "all" }} onClose={() => setExporting(false)} />}
      {naming && <Prompt title="Display name" label="Shown in the Home greeting" initial={name} onSubmit={(v) => void setDisplayName(v)} onClose={() => setNaming(false)} />}
    </div>
  );
}
